import {
  AppBarItem,
  Dropdown,
  IDropdownItem,
  ISelectItem,
  SettingsItem,
  SystemLayer
} from "@react-gcc-eds/core";
import "@react-gcc-eds/core/lib/styling.css";
import { subMinutes } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route, Routes, useNavigate } from "react-router";
import { ICustomerIdentity } from "types";
import { useCustomerActions } from "../actions/customer-actions";
import { useNotificationActions } from "../actions/notification-actions";
import { useScheduledExportsActions } from "../actions/scheduled-exports-actions";
import { useSettingsActions } from "../actions/settings-actions";
import { useUserActions } from "../actions/user-actions";
import {
  INotificationItem,
  IScheduledExport,
  IScheduledExportItem,
  IUser,
  IUserCustomerNotificationItem,
  UserInfo,
  useStore
} from "../store";
import { dateTimeFormatter, stringSorter } from "../utils";
import { useLocalization } from "../utils/localization";
import { useUserHelpers } from "../utils/user-helpers";
import PendingUserRequestsNotification from "./administration/user-access-requests/pending-requests-notification";
import { useMsalFlow } from "./auth/msal-flow";
import { ApiErrorManager, ApplicationError } from "./errors";
import ContactUs, { EViews } from "./mail-forms/contact-us";
import { usePopUpFeedback } from "./mail-forms/pop-up-feedback";
import { NewCustomerForm } from "./mail-forms/registration";
import UserApplicationSettings from "./settings";
import { useApplicationSettingsHelpers } from "./settings/application-settings-helpers";
import ExportDownload from "./shared/export-download";
import NextDeployment from "./shared/next-deployment";
import NotificationsDialog from "./shared/notifications-dialog/notifications-dialog";
import RedirectionView from "./shared/redirection-view";
import { useScheduledExportHelpers } from "./shared/scheduled-exports/scheduled-export-helpers";
import ScheduledExportsDialog from "./shared/scheduled-exports/scheduled-exports-dialog/scheduled-exports-dialog";
import StartupMessage from "./shared/startup-message";
import { PersistTheme } from "./shared/user-preferences";
import VirtualRoleBanner from "./shared/virtual-role-banner";
import { ApplicationRoutes } from "./shared/routes-helpers";
import { ModuleRegistry, provideGlobalGridOptions } from "ag-grid-community";
import { AllEnterpriseModule } from "ag-grid-enterprise";

// AG Grid module registration.
ModuleRegistry.registerModules([AllEnterpriseModule]);
provideGlobalGridOptions({
  theme: "legacy"
});

/**
 * The Application component renders for all routes after completing log-in.
 */
const Application = ({ user }: { user: IUser }) => {
  const [state] = useStore();
  const ref = useRef<HTMLDivElement>(null);
  const [features, toggleFeatures] = useState({ feedback: false, support: false, faq: false });
  const [showContact, setShowContact] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  // const [notificationsLength, setNotificationsLength] = useState<number>(0);
  const [showNotificationsDialog, setShowNotificationsDialog] = useState(false);
  const [showScheduledExportsDialog, setShowScheduledExportsDialog] = useState(false);
  const [viewUntouched, setViewUntouched] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark" | undefined>(
    state.userPreferences.theme
  );
  const navigate = useNavigate();
  const { resetUser, getUserRoles, getUserDateFormats } = useUserActions();
  const {
    getUserCustomerNotificationsList,
    getAdminCustomerNotificationsList,
    updateUserCustomerNotificationsList,
    updateUserCustomerNotificationsEnabled,
    getUserCustomerNotificationsEnabled
  } = useNotificationActions();
  const {
    getAdminCustomerScheduledExportsList,
    updateUserCustomerScheduledExportsList,
    getUserCustomerScheduledExportsEnabled
  } = useScheduledExportsActions();
  const { getCustomerNotifications } = useCustomerActions();
  const { getCustomerScheduledExports } = useCustomerActions();
  const {
    userRoleIsSuperUser,
    userRoleIsAdministrator,
    userRoleIsTechSupport,
    userRoleIsInternal
  } = useUserHelpers();
  const { currentCustomer } = useSettingsActions();
  const { PopUpFeedback } = usePopUpFeedback();
  const { localeString } = useLocalization();
  const {
    handleCustomerChanged,
    handleVirtualRoleChanged,
    handleUserPrefChanged,
    handleDateFormatChanged
  } = useApplicationSettingsHelpers();
  const [userCustomerNotifications, setUserCustomerNotifications] = useState<{
    current: IUserCustomerNotificationItem[];
    original: IUserCustomerNotificationItem[];
    equals: boolean;
  }>({ current: [], original: [], equals: true });
  const [adminCustomerNotifications, setAdminCustomerNotifications] = useState<INotificationItem[]>(
    []
  );
  const [adminCustomerScheduledExports, setAdminCustomerScheduledExports] = useState<
    IScheduledExportItem[]
  >([]);
  const [userCustomerScheduledExports, setUserCustomerScheduledExports] = useState<{
    current: IScheduledExport;
    original: IScheduledExport;
    equals: boolean;
  }>({
    current: { recurringDay: 0, scheduledExportUserCustomerSettings: [] },
    original: { recurringDay: 0, scheduledExportUserCustomerSettings: [] },
    equals: true
  });
  const {
    handleUserCustomerScheduledExportsEnabled,
    handleUserCustomerScheduledExportsOptionsChanged,
    handleUserScheduledExportsEnabled,
    getUserCustomerScheduledExports,
    handleWeeklyChecked,
    handleWeekdaySelected
  } = useScheduledExportHelpers({ setUserCustomerScheduledExports });
  const { useAutoLogout, signOut } = useMsalFlow();

  const onLogOut = () => {
    resetUser();
    signOut();
  };

  const isAdminRole = (user: IUser) => {
    if (
      userRoleIsSuperUser(user.role) ||
      userRoleIsAdministrator(user.role) ||
      userRoleIsTechSupport(user.role)
    )
      return true;
    return false;
  };

  const handleUserCustomerNotificationsEnabled = (key: number, value: boolean) => {
    const replaced = (
      notifications: IUserCustomerNotificationItem[],
      key: number,
      value: boolean
    ) => notifications.map(d => (d.notificationId === key ? { ...d, enabled: value } : d));

    const areEqual = (
      a?: IUserCustomerNotificationItem[] | IScheduledExport | IScheduledExportItem[],
      b?: IUserCustomerNotificationItem[] | IScheduledExport | IScheduledExportItem[]
    ) => {
      if (!a || !b) return false;
      return JSON.stringify(a) === JSON.stringify(b);
    };

    setUserCustomerNotifications(notifications => ({
      ...notifications,
      current: replaced(notifications.current, key, value),
      equals: areEqual(replaced(notifications.current, key, value), notifications.original)
    }));
  };
  const handleUserNotificationsEnabled = (customerId: string, value: boolean) => {
    if (customerId) updateUserCustomerNotificationsEnabled(customerId, value);
  };
  const getUserCustomerNotifications = async (customerId?: string) => {
    if (customerId) {
      const notifications = await getUserCustomerNotificationsList();
      if (notifications)
        setUserCustomerNotifications({
          current: notifications,
          original: notifications,
          equals: true
        });
    }
  };

  useEffect(() => {
    if (state.customer) {
      getUserCustomerNotificationsEnabled();
      getUserCustomerScheduledExportsEnabled(state.customer.id);
      getCustomerNotifications(state.customer.id);
      getCustomerScheduledExports(state.customer.id);
      getUserCustomerNotifications(state.customer.id);
      getUserCustomerScheduledExports(state.customer.id);
    }
  }, [state.customer, state.notifications, state.customerNotification]);

  useEffect(() => {
    setUserCustomerNotifications({
      current: state.userCustomerNotifications,
      original: state.userCustomerNotifications,
      equals: true
    });
  }, []);

  useEffect(() => {
    setUserCustomerScheduledExports({
      current: state.scheduledExports,
      original: state.scheduledExports,
      equals: true
    });
  }, []);

  const configureFeatures = async () => {
    const f = { ...features };
    const { settings } = state;
    if (settings && settings.Mails) {
      if (settings.Mails.Feedback !== "off") {
        f.feedback = true;
      }
      if (settings.Mails.Support !== "off") {
        f.support = true;
      }
    }

    if (settings && settings.Features) {
      if (settings.Features.FaQ !== "off") {
        f.faq = true;
      }
    }

    toggleFeatures(f);
  };

  const userHasRole = () => {
    const user = state.user;
    const visibleItems = state.notifications.filter(x => x.visible);
    const userRole = visibleItems.some(
      x => x.roles.length === 0 || x.roles.some(y => y === user?.role)
    );
    return userRole;
  };

  const userHasScheduledExportRole = () => {
    const user = state.user;
    const exportItems = state.adminCustomerScheduleExports;
    const userRole = exportItems.some(
      x => x.roles.length === 0 || x.roles.some(y => y === user?.role)
    );
    return userRole;
  };
  const handleNotificationScopeChanged = (
    i: number,
    item?: IUserCustomerNotificationItem | undefined
  ) => {
    const n = userCustomerNotifications.current.map(n =>
      item && n.notificationId === item.notificationId ? { ...n, notificationScope: i } : n
    );
    setUserCustomerNotifications({
      current: n,
      original: n,
      equals: true
    });
  };

  const handleOnDayChanged = (i: number) => {
    const n = userCustomerNotifications.current.map(n =>
      n.recurringDay != i ? { ...n, recurringDay: i } : n
    );
    setUserCustomerNotifications({
      current: n,
      original: n,
      equals: true
    });
  };
  const handleOnFrequencyChanged = (i: number) => {
    const n = userCustomerNotifications.current.map(n =>
      n.frequency != i ? { ...n, frequency: i } : n
    );
    setUserCustomerNotifications({
      current: n,
      original: n,
      equals: true
    });
  };

  const handleUserCustomerNotificationsSave = () => {
    if (state.customer) {
      updateUserCustomerNotificationsList(state.customer.id, userCustomerNotifications.current);
    }
  };

  const handleUserCustomerScheduledExportsSave = () => {
    if (state.customerScheduledExports[0].frequency === 0) {
      handleWeeklyChecked(false, 0);
      if (state.customer) {
        updateUserCustomerScheduledExportsList(
          state.customer.id,
          userCustomerScheduledExports.current
        );
      }
    } else {
      if (state.customer) {
        updateUserCustomerScheduledExportsList(
          state.customer.id,
          userCustomerScheduledExports.current
        );
      }
    }
  };

  useEffect(() => {
    getUserRoles();
    getUserDateFormats();
    if (state.customer) {
      getAdminCustomerNotificationsList(state.customer.id);
      getAdminCustomerScheduledExportsList(state.customer.id);
    }
  }, []);

  useEffect(() => {
    setAdminCustomerScheduledExports(state.adminCustomerScheduleExports);
  }, [state.adminCustomerScheduleExports]);

  useEffect(() => {
    setAdminCustomerNotifications(state.notifications);
    setUserCustomerNotifications({
      current: userCustomerNotifications.current,
      original: userCustomerNotifications.current,
      equals: true
    });
  }, [state.notifications, state.userCustomerNotifications]);

  useEffect(() => {
    configureFeatures();
  }, [state.settings]);

  const toggleShowNewCustomerForm = () => setShowNewCustomer(old => !old);
  const toggleNotificationsDialog = () => setShowNotificationsDialog(show => !show);
  const toggleScheduledExportsDialog = () => setShowScheduledExportsDialog(show => !show);

  const toggleContact = () => setShowContact(show => !show);

  const appBarItems = () => {
    //TODO: hidden since needs to be reworked
    //const items = [<GlobalSearchInput key="search" />];
    return [
      <AppBarItem
        key="version_icon"
        text={`v${state.settings.Version}`}
        onClick={() => navigate("/help/release-notes")}
        tooltip="View release notes"
      />,
      (features.feedback || features.support) && (
        <AppBarItem
          key="contact_us_icon"
          iconName="message-contact-us"
          onClick={toggleContact}
          tooltip="Contact us"
        />
      )
    ].reduce((all: React.ReactElement<any, any>[], curr) => (curr ? [...all, curr] : all), []);
  };

  const dataFreshness = () => {
    const customer = currentCustomer();
    const dataUpdateTime = new Date(customer.dataUpdatedAt);
    const dbSchenkerUpdateTime = new Date(customer.dbSchenkerDataUpdateAt);
    const timeOffset = dataUpdateTime.getTimezoneOffset();
    const dbSchenkerTimeOffset = dbSchenkerUpdateTime.getTimezoneOffset();
    const localTime = subMinutes(dataUpdateTime, timeOffset);
    const dbSchenkerLocalTime = subMinutes(dbSchenkerUpdateTime, dbSchenkerTimeOffset);

    let dataUpdatedAt =
      !customer.isHoldingCompany && customer.dataUpdatedAt
        ? `${localeString("DataRefreshed") || "Data refreshed"}: ${dateTimeFormatter(
            localTime.toISOString(),
            state.user?.dateFormat
          )}`
        : "";
    if (customer.features.applicationSettings.isUsingDbSchenkerFtpIntegration) {
      dataUpdatedAt += customer.dbSchenkerDataUpdateAt
        ? `\nDBSchenker refreshed: ${dateTimeFormatter(
            dbSchenkerLocalTime.toISOString(),
            state.user?.dateFormat
          )}`
        : "";
    }
    return dataUpdatedAt;
  };

  const connectedCustomers = () => {
    if (!state.user) {
      return [];
    }

    const customers = state.user.customers.reduce(
      (all: ICustomerIdentity[], current: ICustomerIdentity) => {
        const customer = state.customers.find(c => c.id === current.id);
        return [...all, customer || current];
      },
      []
    );

    return customers
      .sort(stringSorter<ICustomerIdentity>(c => c.displayName))
      .map(c => ({ title: c.displayName, value: c }));
  };

  const selectedCustomer = () => {
    return state.userPreferences.customerId ? currentCustomer().displayName : "Select customer";
  };

  const manyCustomers = () => state.user && state.user.customers.length > 1;

  const dropDownItem = () => {
    return (
      <SettingsItem text="" className="top-user-pref-item">
        <div className="user-pref-customer-selection">
          <Dropdown
            label={selectedCustomer()}
            items={connectedCustomers().map(c => {
              return {
                title: c.title,
                value: c.value.id.toString()
              } as ISelectItem;
            })}
            onItemClick={(c: IDropdownItem) => {
              handleCustomerChanged(c.value);
            }}
            disabled={!manyCustomers()}
          />
        </div>
      </SettingsItem>
    );
  };
  const userInfo = useMemo(
    () => ({
      firstName: user ? user.firstName : "",
      lastName: user ? user.lastName : "",
      header: dropDownItem(),
      subHeader: dataFreshness()
    }),
    [user, currentCustomer()]
  );

  useAutoLogout(
    !import.meta.env.DEV && state.userRoles.some(r => r) && !userRoleIsInternal(user.role)
  );

  return (
    <Routes>
      <Route key="export-download" path="export/download" element={<ExportDownload />} />
      <Route key="redirect" path="redirect" element={<RedirectionView />} />
      <Route
        key="App"
        path="*"
        element={
          <SystemLayer
            acronym="ECOD"
            applicationName={`Ericsson Customer Order Dashboard${
              currentCustomer().displayName ? ` - ${currentCustomer().displayName}` : ""
            }`}
            user={userInfo as UserInfo}
            theme={currentTheme}
            persistTheme={false}
            settingsContent={[
              <UserApplicationSettings
                key="stx"
                onNewCustomerPressed={toggleShowNewCustomerForm}
                onVirtualRoleChanged={handleVirtualRoleChanged}
                onUserPrefChanged={handleUserPrefChanged}
                onDateFormatChanged={handleDateFormatChanged}
                onShowNotificationsDialog={toggleNotificationsDialog}
                onUserNotificationsEnabled={handleUserNotificationsEnabled}
                notificationsEnabled={state.userCustomerNotificationsEnabled}
                onShowScheduledExportsDialog={toggleScheduledExportsDialog}
                onUserScheduledExportsEnabled={handleUserScheduledExportsEnabled}
                scheduledExportsEnabled={state.userCustomerScheduledExportsEnabled}
              />
            ]}
            onLogout={onLogOut}
            onBrandClick={() => navigate("/")}
            appBarItems={appBarItems()}
          >
            <ApiErrorManager />
            <PersistTheme onThemeChanged={setCurrentTheme} />
            <StartupMessage />
            <NextDeployment />
            {currentCustomer().showFeedbackForm && <PopUpFeedback />}
            {/*<PopUpFeedback />*/
            /*this is to force show ecodSpecialUserNotificationDialog*/}
            <PendingUserRequestsNotification />
            {showNewCustomer && <NewCustomerForm onClose={toggleShowNewCustomerForm} />}
            <VirtualRoleBanner
              role={state.user ? state.user.virtualRole : null}
              onRestoreRole={() => handleVirtualRoleChanged(null)}
            />
            {showContact && (
              <ContactUs
                customerIdentifier={currentCustomer().identifier}
                onClose={toggleContact}
                typeOfForm={EViews.FEEDBACK}
              />
            )}
            {showNotificationsDialog &&
              state.user &&
              (userHasRole() || isAdminRole(state.user)) && (
                <NotificationsDialog
                  onClose={toggleNotificationsDialog}
                  onFrequencyChanged={handleOnFrequencyChanged}
                  onNotificationScopeChanged={handleNotificationScopeChanged}
                  onDayChanged={handleOnDayChanged}
                  notificationsList={userCustomerNotifications}
                  adminCustomerNotificationsList={adminCustomerNotifications}
                  onNotificationsEnabled={handleUserCustomerNotificationsEnabled}
                  viewChanged={viewUntouched}
                  onSave={handleUserCustomerNotificationsSave}
                />
              )}
            {showScheduledExportsDialog &&
              state.user &&
              (userHasScheduledExportRole() || isAdminRole(state.user)) && (
                <div className="scheduled-export-dialog">
                  <ScheduledExportsDialog
                    onClose={toggleScheduledExportsDialog}
                    adminCustomerExportItems={adminCustomerScheduledExports}
                    userCustomerScheduledExportItemsList={userCustomerScheduledExports}
                    onScheduledExportsEnabled={handleUserCustomerScheduledExportsEnabled}
                    onScheduledExportViewChanged={handleUserCustomerScheduledExportsOptionsChanged}
                    onWeeklyChecked={handleWeeklyChecked}
                    onWeekdaySelected={handleWeekdaySelected}
                    viewChanged={viewUntouched}
                    onSave={handleUserCustomerScheduledExportsSave}
                  />
                </div>
              )}
            <ErrorBoundary FallbackComponent={ApplicationError}>
              <ApplicationRoutes />
            </ErrorBoundary>
          </SystemLayer>
        }
      />
    </Routes>
  );
};

export default Application;
